import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { apiLogin, apiAdminLogin, apiVerifyLoginOtp, apiVerifyEmail, apiLogout, apiGetMe, apiRefreshToken } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState(
    () => localStorage.getItem('access_token') || null
  );
  const [refreshToken, setRefreshToken] = useState(
    () => localStorage.getItem('refresh_token') || null
  );
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const storeAuth = (tokens, userData) => {
    localStorage.setItem('access_token', tokens.access_token);
    localStorage.setItem('refresh_token', tokens.refresh_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setAccessToken(tokens.access_token);
    setRefreshToken(tokens.refresh_token);
    setUser(userData);
  };

  // Mémorise le jeton d'appareil de confiance renvoyé après un login 2FA complet.
  // Tant qu'il est valide, les prochaines connexions sautent l'OTP email : 30 j
  // pour une sportive ou un coach, 7 j pour un compte d'administration.
  const storeDeviceToken = (tokens) => {
    if (tokens?.device_token) {
      localStorage.setItem('device_token', tokens.device_token);
    }
  };

  // Étape 1 du login 2FA : credentials (+ device_token si connu) → soit le backend
  // saute l'OTP et renvoie directement les tokens (appareil de confiance), soit il
  // répond 202 step=otp_required et un code est envoyé par email.
  // Retourne { loggedIn: true, profile } si la connexion est terminée directement.
  const requestLoginOtp = async (email, password) => {
    const deviceToken = localStorage.getItem('device_token');
    const res = await apiLogin(email, password, deviceToken);
    if (res?.access_token) {
      storeDeviceToken(res);
      const profile = await apiGetMe(res.access_token);
      storeAuth(res, profile);
      return { loggedIn: true, profile };
    }
    return { loggedIn: false };
  };

  // Même contrat de retour que `requestLoginOtp` : un appareil de confiance
  // valide (7 j côté admin) termine la connexion sans passer par l'OTP.
  const requestAdminLoginOtp = async (email, password, adminToken) => {
    const deviceToken = localStorage.getItem('device_token');
    const res = await apiAdminLogin(email, password, adminToken, deviceToken);
    if (res?.access_token) {
      storeDeviceToken(res);
      const profile = await apiGetMe(res.access_token);
      storeAuth(res, profile);
      return { loggedIn: true, profile };
    }
    return { loggedIn: false };
  };

  // Étape 2 : vérifie le code OTP et termine le login (commune sportive/coach/admin).
  const verifyOtpAndLogin = async (email, code) => {
    const tokens = await apiVerifyLoginOtp(email, code);
    storeDeviceToken(tokens);
    const profile = await apiGetMe(tokens.access_token);
    storeAuth(tokens, profile);
    return profile;
  };

  // Vérification email à l'inscription : le code reçu par email fait office de
  // second facteur, le backend renvoie donc directement les tokens → login auto.
  const verifyEmailAndLogin = async (email, code) => {
    const tokens = await apiVerifyEmail(email, code);
    storeDeviceToken(tokens);
    const profile = await apiGetMe(tokens.access_token);
    storeAuth(tokens, profile);
    return profile;
  };

  const logoutUser = async () => {
    const rt = localStorage.getItem('refresh_token');
    if (rt) {
      try { await apiLogout(rt); } catch (_) {}
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
  };

  useEffect(() => {
    const handler = () => {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      setAccessToken(null);
      setRefreshToken(null);
      setUser(null);
    };
    window.addEventListener('auth:unauthorized', handler);
    return () => window.removeEventListener('auth:unauthorized', handler);
  }, []);

  // Au lancement de l'app : tente de restaurer une session à partir des jetons
  // stockés. Le jeton d'accès ne vit que 15 min, donc après une pause il est
  // presque toujours expiré → on enchaîne sur une rotation du refresh token.
  // Renvoie le profil si la session est valide, sinon null (jetons nettoyés).
  const restoreSession = useCallback(async () => {
    // On capture les jetons AVANT tout appel : un 401 déclenche l'event
    // `auth:unauthorized` qui vide localStorage, ce qui nous priverait du
    // refresh token avant d'avoir pu l'utiliser.
    const storedAccess = localStorage.getItem('access_token');
    const storedRefresh = localStorage.getItem('refresh_token');
    if (!storedAccess && !storedRefresh) return null;

    const applyProfile = (profile) => {
      localStorage.setItem('user', JSON.stringify(profile));
      setUser(profile);
      return profile;
    };

    // 1) Essai direct avec le jeton d'accès courant.
    if (storedAccess) {
      try {
        return applyProfile(await apiGetMe(storedAccess));
      } catch (_) {
        // Jeton d'accès expiré/invalide → on tente une rotation du refresh.
      }
    }

    // 2) Rotation du refresh token puis nouvelle tentative.
    if (storedRefresh) {
      try {
        const tokens = await apiRefreshToken(storedRefresh);
        const profile = await apiGetMe(tokens.access_token);
        localStorage.setItem('access_token', tokens.access_token);
        localStorage.setItem('refresh_token', tokens.refresh_token);
        setAccessToken(tokens.access_token);
        setRefreshToken(tokens.refresh_token);
        return applyProfile(profile);
      } catch (_) {
        // Refresh échoué → session morte, on nettoie ci-dessous.
      }
    }

    // 3) Aucune session valide : nettoyage complet.
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
    return null;
  }, []);

  const getToken = useCallback(() => accessToken, [accessToken]);

  const updateUser = useCallback((newProfile) => {
    localStorage.setItem('user', JSON.stringify(newProfile));
    setUser(newProfile);
  }, []);

  return (
    <AuthContext.Provider value={{ user, accessToken, getToken, restoreSession, requestLoginOtp, requestAdminLoginOtp, verifyOtpAndLogin, verifyEmailAndLogin, logoutUser, storeAuth, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
